import { Button, FormTitle, Tag, Tooltip, Wrapper, message, useBreadcrumbRoutes } from '@maxtropy/components';
import { ReactNode, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMockAlarm } from '../mockData';
import { AlarmLevel, MonitorType } from '../types';
import styles from './index.module.scss';

const ALARM_LEVEL_CONFIG: Record<AlarmLevel, { label: string; color: string }> = {
  [AlarmLevel.LOWEST]: { label: '最低级', color: '#A0D911' },
  [AlarmLevel.LOW]: { label: '低级', color: '#52C41A' },
  [AlarmLevel.MEDIUM]: { label: '中级', color: '#2D8DFF' },
  [AlarmLevel.HIGH]: { label: '高级', color: '#FAAD14' },
  [AlarmLevel.HIGHEST]: { label: '最高级', color: '#FF4D4F' },
};

const MONITOR_TYPE_CONFIG: Record<MonitorType, string> = {
  [MonitorType.PV]: '光伏',
  [MonitorType.LOAD]: '负荷',
  [MonitorType.THERMAL]: '火电',
  [MonitorType.STORAGE]: '储能',
};

interface DetailItemProps {
  label: string;
  value: ReactNode;
  tooltip?: string;
}

const DetailItem = ({ label, value, tooltip }: DetailItemProps) => {
  const content = <span className={styles.value}>{value}</span>;

  return (
    <div className={styles.detailItem}>
      <span className={styles.label}>{label}：</span>
      {tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content}
    </div>
  );
};

const AlarmDetail = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const record = id ? getMockAlarm(Number(id)) : undefined;

  useEffect(() => {
    if (record) {
      return;
    }

    message.warning('未找到该报警记录');
    navigate('/dispatch/control/deviation-alarm', { replace: true });
  }, [navigate, record]);

  if (!record) {
    return null;
  }

  const alarmLevel = ALARM_LEVEL_CONFIG[record.alarmLevel];
  const alarmInfo = `实际执行与调度建议偏差率大于 ${record.threshold.toFixed(2)}%`;

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '查看报警' }]} hasPadding={false}>
      <main className={styles.page}>
        <FormTitle title="查看报警" className={styles.pageTitle} />
        <div className={styles.detailGrid}>
          <DetailItem label="报警信息" value={alarmInfo} tooltip={alarmInfo} />
          <DetailItem label="监听对象类型" value={MONITOR_TYPE_CONFIG[record.monitorType]} />
          <DetailItem label="监听对象" value={record.monitorObject} tooltip={record.monitorObject} />

          <DetailItem
            label="报警等级"
            value={
              <Tag border="solid" color={alarmLevel.color} className={styles.tag}>
                {alarmLevel.label}
              </Tag>
            }
          />
          <DetailItem label="报警规则" value={<span className={styles.linkValue}>{record.alarmRuleCode}</span>} />
          <DetailItem label="报警时间" value={record.alarmTime} />

          <DetailItem label="报警规则编号" value={<span className={styles.linkValue}>{record.alarmRuleCode}</span>} />
          <DetailItem label="报警时间" value={record.alarmTime} />
          <DetailItem label="恢复时间" value={record.recoveryTime} />

          <DetailItem label="调度建议" value={`${record.dispatchSuggestion.toFixed(1)}kW`} />
          <DetailItem
            label="实际执行"
            value={<span className={styles.successValue}>{record.actualExecution.toFixed(0)}kW</span>}
          />
          <DetailItem
            label="偏差率"
            value={<span className={styles.warningValue}>{record.deviationRate.toFixed(2)}%</span>}
          />
        </div>
        <div className={styles.actions}>
          <Button onClick={() => navigate('/dispatch/control/deviation-alarm')}>返回</Button>
        </div>
      </main>
    </Wrapper>
  );
};

export default AlarmDetail;
